<script setup lang="ts">
import VehicelsTemperatureList from "@/components/TemperatureInformationNumerical/VehicelsTemperatureList.vue";
import subscribeService from '@/services/subscribe/SubscribeServiceFabrick'
import vehicle from '@/types/vehicle'
import { Sensor } from '@/API'
import SearchForm from "@/components/TemperatureInformationNumerical/SearchForm.vue"
import searchVehicleResponse from "@/types/responses/searchVehicle"
import getTemperatureInformationVehicle from "@/types/requests/getTemperatureInformationVehicle"
import { Ref, ref, reactive, onMounted, onUnmounted, watch } from 'vue'
import Api from "@/services/api/ApiServiceFabrick"
import { useAuth } from "@/services/user/Auth0UserProvider"
import i18n from '@/lang/i18n'
import PaginateWithCount from "@/components/PaginateWithCount.vue"
import getDepartment from "@/types/responses/getDepartment";
import getVehicle from "@/types/responses/getVehicle";
import temperatureInformationSpotRequest from "@/types/requests/temperatureInformationSpot";
import temperatureInformationSpotResponse from "@/types/responses/temperatureInformationSpot";
import { formatDateTime } from "@/services/functions";
import { DEPARTMENT_OFFICE_TYPE } from "@/services/consts"

document.title = i18n.global.t('pages.temperatureSensors.informationNumerical.title');
const { loginWithRedirect } = useAuth();
const currentPage = ref(1);
const totalPage = ref(0);
const isLoading = ref(false);
const vehicles: Ref<searchVehicleResponse["items"]> = ref([]);
const beforeSearchParams: Ref<getTemperatureInformationVehicle | null> = ref(null);
const searchParams = reactive({
  page: 1,
  sort: null,
  sort_type: null,
});
const windowInnerWidth = ref(window.innerWidth);
const spots: Ref<temperatureInformationSpotResponse[]> = ref([])
const updatePositions: Ref<{
  [device_id: string]: {
    unique_key: string,
    lat: number,
    lon: number,
  }
}> = ref([])
const subscribers:Ref<any> = ref([])
const isDepartment: Ref<boolean> = ref(false);
const selfId: Ref<number | undefined> = ref()

const POLLING_INTERVAL = 5000

type filterParamsType = {
  department: getDepartment | undefined,
  vehicle: getVehicle | undefined
} | null

window.addEventListener('resize', handleResize);

let pollingSpotTimer = null;
onMounted(() => {
  getVehicles()
  getSelf()
  pollingSpotInfo()
})

onUnmounted(() => {
  closeSubscribers()
  if (pollingSpotTimer !== null) clearInterval(pollingSpotTimer)
})

function handleResize() {
  windowInnerWidth.value = window.innerWidth;
}

function changeSortOrder(sortItem: any, sortType: any) {
  searchParams.sort = sortItem;
  searchParams.sort_type = sortType;
  if (currentPage.value == 1) return getVehicles();
  currentPage.value = 1;
}

function closeSubscribers() {
  subscribers.value.forEach((subscriber) => {
    subscriber.unsubscribe()
  })
}

function getSelf() {
  Api().getSelfAccount().then((data) => {
    isDepartment.value = data.login_organization_type == DEPARTMENT_OFFICE_TYPE
  })
}

function getVehicles(filterParams: filterParamsType = null, page = 1, updateOption = true) {
  isLoading.value = true
  updatePositions.value = {}
  closeSubscribers()
  let req: getTemperatureInformationVehicle

  if (updateOption || !beforeSearchParams.value) {
    req = makeSearchParams(filterParams, page);
    beforeSearchParams.value = req;
  } else {
    req = beforeSearchParams.value;
    req.page = page;
  }

  Api().searchVehicles(req).then((data) => {
    vehicles.value = data.items
    currentPage.value = data.current_page
    totalPage.value = data.total_page
    isLoading.value = false
    data.items.forEach((vehicle, index) => {
      if (vehicle.device) {
        const subscriber = subscribeService().subscribeToVehicleSubscriptions(
          { device_id: vehicle.device.device_id },
          (vehicle: vehicle, sensor: Sensor) => {
            setVehicleSensor(vehicle, sensor, index)
          }
        )
        subscribers.value.push(subscriber)
      }
    })
    setLatestSensors();
  }).catch((error) => {
    errorHandle(error.response);
  })
}

async function setLatestSensors() {
  const deviceIds = [];
  vehicles.value.forEach((vehicle) => {
    if (!vehicle.device?.device_id) return;
    deviceIds.push(vehicle.device.device_id);
  })
  const sensors = await Api().getLatestSensors({device_ids: deviceIds})
  sensors.forEach((sensor) => {
    vehicles.value.forEach((vehicle, index) => {
      if (!vehicle.device?.device_id) return;
      if (sensor.sn == vehicle.device.device_id) {
        sensor.tm = sensor.tm * 1000;
        setVehicleSensor({device_id: vehicle.device.device_id}, sensor, index);
      }
    })
  })
}

const setVehicleSensor = (vehicle: vehicle, sensor: Sensor, vehicleIndex: number) => {
  if (vehicles.value[vehicleIndex]?.device?.device_id == vehicle.device_id) {
    vehicles.value[vehicleIndex].ch1 = sensor.d11
    vehicles.value[vehicleIndex].ch2 = sensor.d21
    vehicles.value[vehicleIndex].ch3 = sensor.d31
    vehicles.value[vehicleIndex].ch4 = sensor.d41
    const position = {
      lat: sensor.lat,
      lng: sensor.lon,
    }
    vehicles.value[vehicleIndex].position = position
    vehicles.value[vehicleIndex].datetime = formatDateTime(new Date(sensor.tm), '/')
    updatePositions.value[vehicle.device_id] = {
      lat: sensor.lat,
      lon: sensor.lon,
      unique_key: vehicle.device_id,
    }
  }
}

function makeSearchParams(filterParams: filterParamsType = null, page = undefined as number | undefined){
  return {
    page: page,
    is_selected_unaffiliated: filterParams?.department?.id === null,
    department_id: filterParams?.department?.id ?? null,
    number_plate: filterParams?.vehicle?.number_plate,
    sort: searchParams?.sort,
    sort_type: searchParams?.sort_type,
    enabled_td_sensor: true,
    is_setting_digital_tacograph: true,
  } as getTemperatureInformationVehicle
}

function pollingSpotInfo() {
  pollingSpotTimer = setInterval(() => {
    if (!Object.values(updatePositions.value)[0]) return
    getUpdatedSpots()
  }, POLLING_INTERVAL)
}

function getUpdatedSpots() {
  const requestParams: temperatureInformationSpotRequest = makeSpotParams();
  updatePositions.value = {}
  Api().getTemperatureInformationSpot(requestParams).then((data) => {
    data.forEach((d) => {
      vehicles.value.forEach((v, index) => {
        if (v.device?.device_id == d.unique_key) {
          vehicles.value[index].spot_name = d.spot?.name
        }
      })
    })
  }).catch((error) => {
    errorHandle(error.response);
  });
}

function makeSpotParams() {
  return {
    locations: Object.values(updatePositions.value)
  }
}

function errorHandle(res: any) {
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      break
    case 403:
      location.reload()
      break
  }
  isLoading.value = false
}

watch(currentPage, () => {
  getVehicles(null, currentPage.value, false)
})
</script>
<template>
  <div class="content p-4 w-100">
    <div class="d-flex justify-content-between mb-12">
      <h4 class="card-title mt-0">{{ i18n.global.t('pages.temperatureSensors.informationNumerical.title') }}</h4>
    </div>
    <SearchForm
      :loading="isLoading"
      :windowInnerWidth="windowInnerWidth"
      :isDepartment="isDepartment"
      :selfId="selfId"
      @search="getVehicles"
    />
    <VehicelsTemperatureList
      class="mb-2"
      :vehicles="vehicles"
      :spots="spots"
      :isLoading="isLoading"
      @changeSortOrder="changeSortOrder"
    />
    <PaginateWithCount
      class="justify-end"
      :totalPage="totalPage"
      v-model="currentPage"
      :showItemCount="false"
    />
  </div>
</template>
